import { QueryClientProvider } from '@tanstack/react-query';
import { Navigate, createBrowserRouter, RouterProvider } from 'react-router';
import { AppShell } from './app/AppShell';
import { CHANGE_PASSWORD_PATH, RequireAdmin, RequireAuth } from './auth/RequireAuth';
import { ConfirmProvider } from './components/Confirm';
import { ToastProvider } from './components/Toast';
import { onNotificationNavigate } from './lib/pwa';
import { queryClient } from './lib/queryClient';
import { CalendarPage } from './pages/calendar/CalendarPage';
import { ChangePasswordPage, ForcedPasswordChangePage } from './pages/ChangePasswordPage';
import { ClientDetailPage } from './pages/clients/ClientDetailPage';
import { EditClientPage, NewClientPage } from './pages/clients/ClientFormPage';
import { ClientsPage } from './pages/clients/ClientsPage';
import { DuePage } from './pages/closing/DuePage';
import { SummaryPage } from './pages/closing/SummaryPage';
import { UnpaidPage } from './pages/closing/UnpaidPage';
import { ForbiddenPage } from './pages/ForbiddenPage';
import { InstallPage } from './pages/InstallPage';
import { LoginPage } from './pages/LoginPage';
import { DayMapPage } from './pages/map/DayMapPage';
import { MorePage } from './pages/MorePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { RouteErrorPage } from './pages/RouteErrorPage';
import { SettingsPage } from './pages/SettingsPage';
import { ServiceTypesPage } from './pages/settings/ServiceTypesPage';
import { EditUserPage, NewUserPage } from './pages/users/UserFormPage';
import { UsersPage } from './pages/users/UsersPage';
import { VisitDetailPage } from './pages/visits/VisitDetailPage';
import { EditVisitPage, NewVisitPage } from './pages/visits/VisitFormPage';

const router = createBrowserRouter([
  { path: '/logowanie', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: CHANGE_PASSWORD_PATH, element: <ForcedPasswordChangePage /> },
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/kalendarz" replace /> },
          { path: 'kalendarz', element: <CalendarPage /> },
          { path: 'do-umowienia', element: <DuePage /> },
          { path: 'klienci', element: <ClientsPage /> },
          { path: 'klienci/nowy', element: <NewClientPage /> },
          { path: 'klienci/:id', element: <ClientDetailPage /> },
          { path: 'klienci/:id/edycja', element: <EditClientPage /> },
          { path: 'wiecej', element: <MorePage /> },
          { path: 'wizyty/nowa', element: <NewVisitPage /> },
          { path: 'wizyty/:id', element: <VisitDetailPage /> },
          { path: 'wizyty/:id/edycja', element: <EditVisitPage /> },
          { path: 'mapa', element: <DayMapPage /> },
          { path: 'nieoplacone', element: <UnpaidPage /> },
          { path: 'ustawienia', element: <SettingsPage /> },
          { path: 'ustawienia/haslo', element: <ChangePasswordPage /> },
          { path: 'instalacja', element: <InstallPage /> },
          { path: 'brak-dostepu', element: <ForbiddenPage /> },
          {
            element: <RequireAdmin />,
            children: [
              { path: 'uzytkownicy', element: <UsersPage /> },
              { path: 'uzytkownicy/nowy', element: <NewUserPage /> },
              { path: 'uzytkownicy/:id', element: <EditUserPage /> },
              { path: 'podsumowanie', element: <SummaryPage /> },
              { path: 'ustawienia/uslugi', element: <ServiceTypesPage /> },
            ],
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

// A tapped notification opens its screen in the window that is already open.
onNotificationNavigate((url) => void router.navigate(url));

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfirmProvider>
          <RouterProvider router={router} />
        </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
